import React, {useEffect, useState} from 'react';
import {useTranslation} from "react-i18next";
import {toast} from "react-toastify";
import {ResponseResultCode} from "../../../../../app/enums/ResponseResultCode";
import ToastLayout from "../../../../../components/uiLayouts/ToastLayout/ToastLayout";
import {isAxiosError} from "axios";
import FormLoadingScreen from "../../../../../components/ui/loaders/FormLoadingScreen/FormLoadingScreen";
import {INoteAction} from "../../../../../app/interfaces/offer/INoteAction";
import {useOfferApi} from "../../../../../app/api/offer";
import Button, {ButtonType} from "../../../../../components/ui/Button/Button";
import ChangeNoteForm from "./ChangeNoteForm/ChangeNoteForm";

type ChangeNoteProps = {
    id: string;
    close?: () => void;
}

const ChangeNote: React.FC<ChangeNoteProps> = ({
                                                   id,
                                                   close
                                               }) => {
    const {t} = useTranslation();
    const [disabled, setDisabled] = useState<boolean>(false);
    const [model, setModel] = useState<INoteAction | null>(null);

    const {
        getChangeNoteData: {
            query: getChangeNoteData,
            isLoading,
            cancel: cancelGetChangeNoteData
        },
        saveNote: {
            mutation: saveNote,
            cancel: cancelSaveNote
        }
    } = useOfferApi();

    useEffect(() => {
        if (id === '') {
            if (close) {
                close()
            } else {
                return;
            }
        }

        (async () => {
            try {
                const response = await getChangeNoteData(id);
                if (response?.status === 200 && response.data && response.data.data && response.data.data) {
                    setModel(response.data.data);
                }
            } catch {
                if (close) {
                    close();
                } else {
                    toast.error<string>('Unable to get note data');
                }
            }
        })();

        return () => {
            cancelSaveNote();
            cancelGetChangeNoteData();
        }

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id]);

    const save = async (model: INoteAction): Promise<void> => {
        setDisabled(prev => !prev);

        try {
            const response = await saveNote(model);

            if (response?.status === 200 && response.data.resultCode === ResponseResultCode.Ok) {
                if (close) {
                    close();
                }

                document.dispatchEvent(new CustomEvent('onGridMessage', {
                    detail: {
                        action: 'updateRows',
                        rows: [
                            {
                                id: id,
                                isRowLoading: true
                            }
                        ]
                    }
                }));

                toast.success(<ToastLayout text={t("shared.labels.delayed-update-warning")}
                                           title={`${t("shared.labels.offer")} ${id}`}/>);
            }
        } catch (err) {
            if (isAxiosError(err) && close) {
                close();
                toast.error(err.message);
            }
        }

        setDisabled(prev => !prev);
    }

    if (isLoading || model === null) {
        return (
            <FormLoadingScreen height={`200px`} style={{padding: '5px'}}/>
        )
    }

    return (
        <div className={'h100 w100'}>
            <div className={'dialog-default-content-container'}>
                <ChangeNoteForm model={model}
                                disabled={disabled}
                                save={save}/>
            </div>
            <div className={'dialog-default-buttons-container'}>
                {close &&
                    <Button buttonType={ButtonType.Transparent}
                            disabled={disabled}
                            className={'close-button'}
                            onClick={close}>
                        {t("shared.btn.cancel")}
                    </Button>
                }
                <Button buttonType={ButtonType.Primary}
                        disabled={disabled}
                        form='offer-change-note-form'
                        type='submit'
                        style={{minWidth: '150px'}}
                        className={'confirm-button'}
                        isLoading={disabled}>
                    {t("shared.btn.submit")}
                </Button>
            </div>
        </div>
    );
};

export default ChangeNote;